import React, { forwardRef, useMemo } from "react";
import Avatar from "@material-ui/core/Avatar";
import { useTheme } from "@material-ui/core/styles";
import PersonIcon from "@material-ui/icons/Person";
import ghostUserAvatarLight from "../../assets/ghost-user-avatar-light.png";
import ghostUserAvatarDark from "../../assets/ghost-user-avatar-dark.png";

const buildAbsoluteUrl = (baseUrl, relativePath) => {
  if (!baseUrl) return relativePath;

  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = relativePath.startsWith("/")
    ? relativePath
    : `/${relativePath}`;

  return `${normalizedBase}${normalizedPath}`;
};

const normalizeSrc = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof value !== "string") return "";

  const trimmed = value.trim();
  if (!trimmed) return "";

  const lower = trimmed.toLowerCase();
  if (
    lower === "null" ||
    lower === "undefined" ||
    lower === "-" ||
    lower === "none"
  ) {
    return "";
  }

  if (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("data:") ||
    lower.startsWith("blob:")
  ) {
    return trimmed;
  }

  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }

  // Bundled frontend assets imported by the build tool should stay local.
  if (trimmed.startsWith("/static/") || trimmed.startsWith("static/")) {
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }

  // Already a path in backend static files.
  if (trimmed.startsWith("/")) {
    return buildAbsoluteUrl(process.env.REACT_APP_BACKEND_URL, trimmed);
  }

  // Handle values like "public/avatar/file.png" or "avatar/file.png".
  if (trimmed.includes("/")) {
    const normalizedPath = trimmed.startsWith("public/")
      ? `/${trimmed}`
      : `/public/${trimmed}`;
    return buildAbsoluteUrl(process.env.REACT_APP_BACKEND_URL, normalizedPath);
  }

  // Avatar images are stored in /public/avatar/
  return buildAbsoluteUrl(
    process.env.REACT_APP_BACKEND_URL,
    `/public/avatar/${trimmed}`,
  );
};

const isGhostUser = (user) => {
  return user?.isGhostUser === true;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const UserAvatar = forwardRef(
  (
    {
      user,
      src,
      alt,
      imgProps,
      style,
      showInitials = true,
      className,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();

    // Allow direct src or extract from user object
    const ghostFallbackSrc = isGhostUser(user)
      ? theme.palette.type === "light"
        ? ghostUserAvatarLight
        : ghostUserAvatarDark
      : "";

    const imageSrc = src || user?.profileImage || ghostFallbackSrc;
    const displayName = alt || user?.name || "";

    const normalizedSrc = useMemo(() => normalizeSrc(imageSrc), [imageSrc]);

    const backgroundColor = theme.palette.primary.main;
    const textColor = theme.palette.primary.contrastText || "#fff";

    const combinedStyle = {
      backgroundColor,
      color: textColor,
      fontWeight: 600,
      ...style,
    };

    return (
      <Avatar
        ref={ref}
        src={normalizedSrc || undefined}
        alt={displayName}
        imgProps={{
          referrerPolicy: "no-referrer",
          ...imgProps,
        }}
        style={combinedStyle}
        className={className}
        {...rest}
      >
        {showInitials && !normalizedSrc ? (
          getInitials(displayName)
        ) : (
          <PersonIcon
            style={{ width: "70%", height: "70%", color: textColor }}
          />
        )}
      </Avatar>
    );
  },
);

UserAvatar.defaultProps = {
  alt: "",
  imgProps: undefined,
  style: undefined,
  showInitials: true,
};

UserAvatar.displayName = "UserAvatar";

export default UserAvatar;
